/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICoreContext, IGeneric, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
// @ts-ignore
import { getCartState } from '@msdyn365-commerce/global-state';
import { action, observable } from 'mobx';

import { getSelectedStoreId } from './cookieutils';
import { getResumeCartId, hasOrderOpenForModify } from './resumeorderformodify';
import { StoreOrderEndedState } from './storeorderended';
import { forgetOrderStatuses, getOpenOrderStoreId, readOpenOrderStatus } from './storeordersync';

/** Whether the venue has changed the open order since this page read it, shared with whatever shows the notice. */
class StoreOrderWatchState {
    @observable public changedAtVenue: boolean = false;

    @action
    public setChangedAtVenue(changedAtVenue: boolean): void {
        this.changedAtVenue = changedAtVenue;
    }
}

export const storeOrderWatchState = new StoreOrderWatchState();

/** The longest the watcher backs off to, as a multiple of its interval. */
const MAX_BACKOFF_FACTOR = 8;

let pushesInFlight = 0;

/** Counts the guest's own pushes, which move the store's marker. */
let pushesSent = 0;

/** Runs a push of the guest's own changes, during which the watcher does not ask the store. */
export const whileStorePushInFlight = async <T>(push: () => Promise<T>): Promise<T> => {
    pushesInFlight += 1;

    try {
        return await push();
    } finally {
        pushesInFlight -= 1;
        pushesSent += 1;
        forgetOrderStatuses();
    }
};

export interface IStoreOrderWatchOptions {
    intervalMs: number;
    onEnded(state: StoreOrderEndedState): void;
}

/**
 * Asks the store on a timer, in one call, whether the venue changed or ended the open order since this page
 * loaded. Raises storeOrderWatchState on a change, hands an ended order to onEnded, and stops either way.
 * @param context - The page's context.
 * @param options - The interval and what to do with a finished order.
 * @returns Stops the watcher.
 */
export const watchOpenOrder = (context: ICoreContext<IGeneric<IAny>>, options: IStoreOrderWatchOptions): (() => void) => {
    if (!msdyn365Commerce.isBrowser || !(options.intervalMs > 0)) {
        return () => undefined;
    }

    let timer: number | undefined;
    let failures = 0;
    let stopped = false;
    let startingMarker: string | undefined;
    let startingAfterPushes = -1;

    const onVisibilityChange = (): void => {
        if (document.hidden) {
            window.clearTimeout(timer);
            timer = undefined;
        } else if (!stopped && timer === undefined) {
            void tick();
        }
    };

    const stop = (): void => {
        stopped = true;
        window.clearTimeout(timer);
        timer = undefined;
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    const schedule = (): void => {
        if (stopped || document.hidden) {
            return;
        }

        timer = window.setTimeout(tick, options.intervalMs * Math.min(2 ** failures, MAX_BACKOFF_FACTOR));
    };

    async function tick(): Promise<void> {
        timer = undefined;

        const openOrderId = getResumeCartId();
        if (stopped || !openOrderId || !hasOrderOpenForModify(getSelectedStoreId() || '')) {
            stop();
            return;
        }

        if (pushesInFlight > 0 || document.hidden) {
            schedule();
            return;
        }

        let cart: Cart | undefined;
        try {
            cart = (await getCartState(context.actionContext)).cart;
        } catch (error) {
            console.log('LTS Info', 'storeorderwatcher: the cart could not be read', error);
        }

        if (!cart || cart.Id !== openOrderId) {
            schedule();
            return;
        }

        const storeOrderId = getOpenOrderStoreId(openOrderId);
        if (!storeOrderId) {
            stop();
            return;
        }

        const pushesBefore = pushesSent;
        const read = await readOpenOrderStatus(storeOrderId, context);

        if (stopped || pushesInFlight > 0 || pushesSent !== pushesBefore) {
            schedule();
            return;
        }

        if (read.ended) {
            stop();
            options.onEnded(read.ended);
            return;
        }

        if (!read.answered) {
            failures += 1;
            console.log('LTS Info', 'storeorderwatcher: the store did not answer, asking less often', { failures });
            schedule();
            return;
        }

        if (read.marker === undefined) {
            stop();
            return;
        }

        failures = 0;

        if (startingAfterPushes !== pushesSent) {
            startingMarker = read.marker;
            startingAfterPushes = pushesSent;
            schedule();
            return;
        }

        if (read.marker === startingMarker) {
            schedule();
            return;
        }

        console.log('LTS Info', 'storeorderwatcher: the venue changed the open order', { openOrderId });
        stop();
        storeOrderWatchState.setChangedAtVenue(true);
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    void tick();

    return stop;
};
